import React, { useMemo, useState } from 'react'
import { Column, FilterType } from 'react-table'
import {
  ActionIcon,
  Alert,
  Badge as CertBadge,
  Button,
  Code,
  Container,
  Flex,
  Group,
  Menu,
  Paper,
  Space,
  Text,
  Title,
} from '@mantine/core'
import {
  DotsHorizontalIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  FileTextIcon,
  InfoCircledIcon,
  MinusIcon,
  PaperPlaneIcon,
  Pencil1Icon,
  RulerHorizontalIcon,
} from '@radix-ui/react-icons'

import { Badge } from '@/apiClient'
import { DataTable } from '@/components/DataTable'
import { Shell } from '@/components/Shell'
import { useReportersQuery } from '@/queries/reporters'
import {
  ChangeReporterLevelModal,
  EditReporterModal,
  InviteReporterModal,
  SetReporterStripeAccountIdModal,
} from '@/routes/Admin/Reporters/Modals'
import {
  ReporterFilterControls,
  ReporterFilters,
} from '@/routes/Admin/Reporters/ReporterFilterControls'
import { useDeactivate } from '@/routes/Admin/Reporters/useDeactivate'
import { useReactivate } from '@/routes/Admin/Reporters/useReactivate'
import { useResendWelcomeEmail } from '@/routes/Admin/Reporters/useResendWelcomeEmail'
import { ReporterBase } from '@/types'
import { getBadgeLabel } from '@/utils/parrot'

// Because of bug in react-table (https://github.com/TanStack/react-table/issues/3930),
// boolean filter does not work correctly so we need to
// do this manually, should be fixed in react-table v8
const createBooleanFilter: (
  atr: keyof ReporterBase,
) => FilterType<ReporterBase> =
  (attribute: keyof ReporterBase) => (rows, _, filterValue: boolean | null) => {
    if (filterValue === null) {
      return rows
    }
    return rows.filter(r => r.values[attribute] === filterValue)
  }

// Reporter matches only if it holds ALL of the selected certifications (AND).
const certificationFilter: FilterType<ReporterBase> = (
  rows,
  _,
  selected: Badge[] | null,
) => {
  if (!selected || selected.length === 0) {
    return rows
  }
  return rows.filter(r => {
    const badges = r.values.certifications as Badge[]
    return selected.every(badge => badges.includes(badge))
  })
}

export const Reporters = () => {
  const [inviting, setInviting] = useState(false)
  const { data: reporters, isLoading, refetch } = useReportersQuery()

  const [filters, setFilters] = useState<ReporterFilters>({
    search: null,
    state: null,
    level: null,
    is_active: true,
    has_documents: null,
    certifications: null,
  })

  const columns = useMemo<Column<ReporterBase>[]>(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone',
        accessor: 'phone_number',
        Cell: ({ value }: { value: string }) =>
          value ? value : <MinusIcon color="gray" />,
      },
      {
        Header: 'Level',
        id: 'level',
        accessor: (r: ReporterBase) => r.level,
      },
      {
        Header: 'State',
        accessor: 'state',
        Cell: ({ value }: { value: string }) =>
          value ? value : <MinusIcon color="gray" />,
      },
      {
        Header: 'Timezone',
        accessor: 'timezone',
        Cell: ({ value }: { value: string }) =>
          value ? value : <MinusIcon color="gray" />,
      },
      {
        Header: 'Documents',
        accessor: 'has_documents',
        Cell: ({ value }: { value: boolean }) => (value ? 'Yes' : 'No'),
        filter: createBooleanFilter('has_documents'),
      },
      {
        Header: 'Certifications',
        id: 'certifications',
        accessor: 'badges',
        filter: certificationFilter,
        Cell: ({ value }: { value: Badge[] }) =>
          value.length ? (
            <div className="flex flex-wrap gap-1">
              {value.map(badge => (
                <CertBadge key={badge} color="grape">
                  {getBadgeLabel(badge)}
                </CertBadge>
              ))}
            </div>
          ) : (
            <MinusIcon color="gray" />
          ),
      },
      {
        id: 'is_active',
        accessor: 'is_active',
        isVisible: false,
        filter: createBooleanFilter('is_active'),
      },
      {
        id: 'actions',
        Cell: ({ row }: { row: { original: ReporterBase } }) => (
          <ReporterRowActions
            reporter={row.original}
            onReporterUpdate={refetch}
          />
        ),
      },
    ],
    [refetch],
  )

  const data = useMemo(() => reporters ?? [], [reporters])

  const { search, ...restFilters } = filters

  return (
    <>
      <Shell header={{ middle: <Title order={6}>Reporters</Title> }}>
        <Container size="xl">
          <Paper p="xl">
            <div className="flex items-start justify-between mb-12">
              <Group className="basis-4/5">
                <ReporterFilterControls
                  reporters={data}
                  onChange={filters => setFilters(filters)}
                  initialValue={filters}
                />
              </Group>
              <div>
                <Button
                  variant="light"
                  color="gray"
                  onClick={() => setInviting(true)}
                >
                  Invite reporter
                </Button>

                <InviteReporterModal
                  opened={inviting}
                  onClose={() => setInviting(false)}
                  onSave={refetch}
                />
              </div>
            </div>

            <DataTable
              data={data}
              columns={columns}
              searchQuery={search}
              filters={restFilters}
              isLoading={isLoading}
            />
            <Space h="lg" />
            <Alert
              mb="md"
              color="blue"
              title="Script training"
              icon={<InfoCircledIcon />}
            >
              <Flex direction="column" gap="sm">
                <div>
                  Reporters can access script in the “training” mode by
                  navigating to <Code>/event/training-event</Code> page. This
                  can be further parametrized in format:
                  <Code block>
                    /event/training-&lt;multiwitness&gt;-&lt;event_type&gt;-&lt;state&gt;-event
                  </Code>
                </div>
                <div>
                  For example, to open script for an EUO event navigate to:
                  <Code block>
                    /event/training-examination-under-oath-event
                  </Code>
                </div>
                <div>
                  to open script for a multiwitness event in NY navigate to:
                  <Code block>/event/training-multiwitness-NY-event</Code> and
                  so on.
                </div>
                <Space h="md" />
                <div>
                  For local Zoom join/leave/reconnect testing, set{' '}
                  <Code>TRAINING_SIM_PARTICIPANTS=file</Code> in backend{' '}
                  <Code>.env</Code> and use{' '}
                  <Code>poetry run training-participants</Code> (see README).
                </div>
                <Text size="xs">
                  note: the training functionality is not available for admins
                </Text>
              </Flex>
            </Alert>
          </Paper>
        </Container>
      </Shell>
    </>
  )
}

const ReporterRowActions: React.FC<{
  reporter: ReporterBase
  onReporterUpdate: () => void
}> = ({ reporter, onReporterUpdate }) => {
  const deactivate = useDeactivate(reporter, onReporterUpdate)
  const reactivate = useReactivate(reporter, onReporterUpdate)
  const resendEmail = useResendWelcomeEmail(reporter)

  const [editing, setEditing] = useState(false)
  const [changingLevel, setChangingLevel] = useState(false)
  const [settingStripeAccount, setSettingStripeAccount] = useState(false)

  return (
    <div className="text-right">
      <Menu position="bottom-start">
        <Menu.Target>
          <ActionIcon>
            <DotsHorizontalIcon />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item icon={<Pencil1Icon />} onClick={() => setEditing(true)}>
            Edit reporter
          </Menu.Item>

          <Menu.Item
            icon={<RulerHorizontalIcon />}
            onClick={() => setChangingLevel(true)}
          >
            Set level
          </Menu.Item>

          <Menu.Item onClick={() => resendEmail()} icon={<PaperPlaneIcon />}>
            Resend welcome mail
          </Menu.Item>

          <Menu.Item
            icon={<FileTextIcon />}
            onClick={() => setSettingStripeAccount(true)}
          >
            Set stripe account ID
          </Menu.Item>

          <Menu.Divider />

          {reporter.is_active ? (
            <Menu.Item onClick={() => deactivate()} icon={<EyeClosedIcon />}>
              Deactivate
            </Menu.Item>
          ) : (
            <Menu.Item onClick={() => reactivate()} icon={<EyeOpenIcon />}>
              Reactivate
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <EditReporterModal
        opened={editing}
        reporter={reporter}
        onClose={() => setEditing(false)}
        onSave={onReporterUpdate}
      />
      <ChangeReporterLevelModal
        opened={changingLevel}
        reporter={reporter}
        onClose={() => setChangingLevel(false)}
        onSave={onReporterUpdate}
      />
      <SetReporterStripeAccountIdModal
        opened={settingStripeAccount}
        reporter={reporter}
        onClose={() => setSettingStripeAccount(false)}
        onSave={onReporterUpdate}
      />
    </div>
  )
}
